import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { twMerge } from 'tailwind-merge'

function EyeIcon({ open }: { open: boolean }) {
  return (
    <svg
      aria-hidden="true"
      className="h-6 w-6"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
      {!open && <path d="m4 4 16 16" />}
    </svg>
  )
}

/**
 * Duolingo's sign-up field: a tall rounded input with a very light fill, the
 * label living in the placeholder, and — for passwords — a reveal eye on the
 * right. Invalid fields take the red outline the rest of the funnel uses.
 */
export default function DuoTextField({
  autoComplete,
  dataTestId,
  invalid,
  name,
  onChange,
  placeholder,
  type,
  value,
  withReveal = false,
}: {
  autoComplete: string
  dataTestId?: string
  invalid?: boolean
  name: string
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  placeholder: string
  type: 'email' | 'password'
  value?: string
  /** Adds the password reveal toggle. */
  withReveal?: boolean
}) {
  const [revealed, setRevealed] = useState(false)

  const { t } = useTranslation()

  return (
    <div className="relative">
      <input
        autoComplete={autoComplete}
        autoFocus
        className={twMerge(
          'border-duo-line text-duo-ink placeholder:text-duo-muted focus:border-duo-primary w-full rounded-2xl border-2 bg-[#F7F7F7] px-4 py-4 text-base font-medium outline-none',
          withReveal && 'pr-14',
          invalid && 'border-duo-red',
        )}
        data-testid={dataTestId}
        name={name}
        onChange={onChange}
        placeholder={placeholder}
        type={withReveal && revealed ? 'text' : type}
        value={value}
      />

      {withReveal && (
        <button
          aria-label={t('pages.signUp.v2.form.togglePassword')}
          className="text-duo-line-dark hover:text-duo-primary absolute top-1/2 right-4 -translate-y-1/2 transition-colors"
          onClick={() => setRevealed((previous) => !previous)}
          type="button"
        >
          <EyeIcon open={revealed} />
        </button>
      )}
    </div>
  )
}
